import {
	Button,
	Box,
	Typography,
	Table,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	// Card, CardContent removed - not used
	TableBody,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { CheckCircleOutline, Repeat } from '@mui/icons-material';
import PastDueLogo from '../../assets/images/pastDueLogo.png';
import { NavLink } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getCategorizedToDos, getToDoPrioritys, DeleteToDo } from 'core/API/todo';
import { deactivateToDoSchedule, getToDoSchedules } from 'core/API/recurringTodo';
import {
	getScheduleAssignmentDisplay,
	getScheduleFrequencyDisplay,
	getScheduleTimeDisplay,
	normalizeToDoSchedules,
} from 'module/toDoPage/utils/recurringTodoUtils';
import { useWorkplaceUser } from 'module/documents/utils';
// TodoWordIcon removed - not used
import { useEffect, useState } from 'react';
import { useDocumentContext } from 'module/documents/context';
import { useUserAssignmentGroupIds } from 'core/hooks/useUserAssignmentGroupIds';
import { useToDoPermissions } from 'module/toDoPage/hooks/useToDoPermissions';
import { filterTodosForMyScope } from 'core/utils/todoAssignment';
import { QueryErrorState } from 'shared/components/QueryErrorState';
import {
	buildPriorityLookup,
	getPriorityDisplayValue,
	getTodoPriorityDisplay,
	getTodoStatusDisplay,
	normalizeAndSortTodos,
	normalizePrioritiesList,
	type TodoTableRow,
} from 'core/utils/todoScheduleEvent';
import { useUserData } from 'store/useUserData';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
// Edit icon not used here
import styles from './Todo.module.scss';
import {
	formatDateMMDDYYYY,
	formatDateTime12Hour,
	// validateDateNotPast,
	// isSameDay,
	getTodayISO,
} from 'shared/utils/dateUtils';
import {
	getTodoDetailLocation,
	getTodoScheduleDetailLocation,
} from 'module/toDoPage/utils/todoNavigation';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

const CONFIRM_DIALOG_PAPER_PROPS = {
	style: {
		backgroundColor: 'white',
		boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
		borderRadius: '8px',
		minWidth: '400px',
	},
};

const CONFIRM_DIALOG_BUTTON_STYLE = {
	backgroundColor: 'rgb(68, 99, 136)',
	color: 'white',
	textTransform: 'none' as const,
	borderRadius: '4px',
	padding: '5px 15px',
};

const Todo = () => {
	const {
		selectedEmployee: _selectedEmployee,
		startDate,
		setStartDate,
		endDate: _endDate,
		setEndDate: _setEndDate,
	} = useDocumentContext();
	const workplace = useWorkplaceUser();
	const workplaceId = getCurrentWorkplaceId() || workplace.data.id;

	const queryClient = useQueryClient();
	const { showToast } = useToastContext();

	// Delete confirmation dialog state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [todoToDelete, setTodoToDelete] = useState<any>(null);
	const [scheduleDeleteDialogOpen, setScheduleDeleteDialogOpen] = useState(false);
	const [scheduleToDelete, setScheduleToDelete] = useState<Record<string, unknown> | null>(null);

	// Single query for categorized todos (includes recurring todo occurrences)
	const {
		data: categorizedTodosData,
		isLoading: isLoadingAdHoc,
		isError: isErrorAdHoc,
		error: errorAdHoc,
		refetch: refetchAdHoc,
	} = useQuery(['todos-categorized', workplaceId], () => getCategorizedToDos({ workplaceId, page: 1, itemsPerPage: 10 }), {
		enabled: !!workplaceId,
		refetchOnMount: 'always',
		refetchOnWindowFocus: false,
		staleTime: 0,
	});

	const {
		data: toDoSchedulesData,
		isLoading: isLoadingSchedules,
		isError: isErrorSchedules,
		error: errorSchedules,
		refetch: refetchSchedules,
	} = useQuery(
		['to-do-schedules', workplaceId],
		() => getToDoSchedules(workplaceId),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	const {
		data: prioritiesData,
		isLoading: isLoadingPriorities,
		isError: isErrorPriorities,
		error: errorPriorities,
		refetch: refetchPriorities,
	} = useQuery(
		['todo-priorities', workplaceId],
		() => getToDoPrioritys(),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	const isLoading = isLoadingAdHoc || isLoadingSchedules || isLoadingPriorities;
	const recurringSchedules = normalizeToDoSchedules(toDoSchedulesData);
	const priorityLookup = buildPriorityLookup(normalizePrioritiesList(prioritiesData));

	// console.log('mytodos', myTodoList, todoList);
	// Permission checks for To-Do module (call hooks before any early returns)
	const {
		canViewMy: canViewToDoMy,
		canViewAll: canViewToDoAll,
		canEdit: canEditToDo,
		canDelete: canDeleteToDo,
		canComplete: canExecuteToDo,
	} = useToDoPermissions();
	const { currentUserRole } = useUserData();
	const currentSecurityRoleId = currentUserRole?.id ?? null;
	const { userGroupIds, isLoading: groupsLoading, isError: isErrorGroups, error: errorGroups, refetch: refetchGroups } =
		useUserAssignmentGroupIds(workplaceId, canViewToDoMy);

	// Delete todo mutation
	const { mutate: deleteTodo, isLoading: isDeleting } = useMutation(
		(id: number) => DeleteToDo(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['todos-categorized', workplaceId]);
				setDeleteDialogOpen(false);
				setTodoToDelete(null);
				showToast('success', 'Todo deleted successfully!');
			},
			onError: (error) => {
				console.error('Failed to delete todo:', error);
				setDeleteDialogOpen(false);
				setTodoToDelete(null);
				showToast('error', 'Failed to delete todo. Please try again.');
			},
		}
	);

	const { mutate: deactivateSchedule, isLoading: isDeactivatingSchedule } = useMutation(
		(scheduleId: number) => deactivateToDoSchedule(scheduleId),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['to-do-schedules', workplaceId]);
				queryClient.invalidateQueries(['todos-categorized', workplaceId]);
				setScheduleDeleteDialogOpen(false);
				setScheduleToDelete(null);
				showToast('success', 'Recurring to-do series deactivated successfully');
			},
			onError: (error) => {
				console.error('Failed to deactivate recurring to-do series:', error);
				setScheduleDeleteDialogOpen(false);
				setScheduleToDelete(null);
				showToast('error', 'Failed to deactivate recurring to-do series');
			},
		}
	);

	useEffect(() => {
		if (!startDate) {
			const storedDate = localStorage.getItem('todo-selected-date');
			const nextDate = storedDate || getTodayISO();
			setStartDate(nextDate);
			if (!storedDate) {
				localStorage.setItem('todo-selected-date', nextDate);
			}
		}
	}, [startDate, setStartDate]);

	if (isLoading || (canViewToDoMy && groupsLoading)) {
		return (
			<Box className={styles.loadingState}>
				<CircularProgress />
			</Box>
		);
	}

	const loadError =
		(isErrorAdHoc && errorAdHoc) ||
		(isErrorSchedules && errorSchedules) ||
		(isErrorPriorities && errorPriorities) ||
		(canViewToDoMy && isErrorGroups && errorGroups) ||
		null;

	if (loadError) {
		return (
			<QueryErrorState
				error={loadError}
				title="Unable to load To-Dos"
				onRetry={() => {
					void refetchAdHoc();
					void refetchSchedules();
					void refetchPriorities();
					if (canViewToDoMy) void refetchGroups();
				}}
			/>
		);
	}

	// Extract data from categorized API response (arrays are directly on root object)
	const myTodos = Array.isArray(categorizedTodosData?.my_todos) ? categorizedTodosData.my_todos : [];
	const outstandingTodos = Array.isArray(categorizedTodosData?.outstanding_todos) ? categorizedTodosData.outstanding_todos : [];
	const pastDueTodos = Array.isArray(categorizedTodosData?.past_due_todos) ? categorizedTodosData.past_due_todos : [];
	const completedTodos = Array.isArray(categorizedTodosData?.completed_todos) ? categorizedTodosData.completed_todos : [];

	const getUserFullName = (user: any): string => {
		// Handle null or undefined user
		if (!user) {
			return 'Unknown User';
		}
		const parts = [user?.firstName, user?.middleName, user?.lastName].filter(
			(p: string | undefined) => !!p && p.trim() !== ''
		);
		return parts.join(' ') || 'Unknown User';
	};

	// parseApiPathId removed - not needed

	// Helper function to format date with time as MM/DD/YYYY HH:MM:SS AM/PM
	const formatTodoDateWithTime = (todo: TodoTableRow): string => {
		if (todo.scheduledDateFormatted && todo.scheduledTimeFormatted) {
			return formatDateTime12Hour(todo.scheduledDateFormatted, todo.scheduledTimeFormatted);
		}
		const dueDateRaw = todo.due_date ?? todo.dueDate;
		const dueTime = todo.dueTime;
		return formatDateTime12Hour(dueDateRaw, dueTime);
	};

	// Helper function to get assignment display value
	const getAssignmentDisplay = (todo: any): string => {
		// Check if user exists and is not null
		if (todo?.user && todo.user !== null) {
			return getUserFullName(todo.user);
		} else if (todo?.assignedGroup) {
			return todo.assignedGroup.label || 'Group Assignment';
		} else if (todo?.assigned) {
			return todo.assigned;
		} else {
			return 'Unassigned';
		}
	};

	// Helper to get a robust description across varying API shapes
	const getDescriptionDisplay = (todo: any): string => {
		const candidates = [
			todo?.description,
			todo?.label,
			todo?.name,
			todo?.title,
			todo?.note?.message,
			todo?.note?.description,
			typeof todo?.note === 'string' ? todo?.note : undefined,
		];
		const val = candidates.find((v: any) => typeof v === 'string' && v.trim() !== '');
		return (val as string) || 'N/A';
	};

	const getCategoryDisplay = (todo: any): string => {
		const category = todo?.category;
		if (!category) return 'Uncategorized';
		if (typeof category === 'string') return category;
		return category?.label || category?.name || category?.title || 'Uncategorized';
	};

	const renderRecurringSeriesIndicator = () => (
		<Box
			component="span"
			className={styles.recurringSeriesBadge}
			title="Part of a recurring to-do series"
		>
			<Repeat sx={{ fontSize: 14 }} />
		</Box>
	);

	const renderDescriptionCell = (todo: TodoTableRow) => {
		const description = getDescriptionDisplay(todo);
		const detailLocation = getTodoDetailLocation(todo.id, { canEdit: canEditToDo });

		const content = todo.isScheduleEvent ? (
			<Box
				display="flex"
				alignItems="center"
				gap={0.75}
				className={styles.descriptionWithIndicator}
			>
				<span className={styles.descriptionText}>{description}</span>
				{renderRecurringSeriesIndicator()}
			</Box>
		) : (
			<span>{description}</span>
		);

		return (
			<NavLink
				to={detailLocation}
				className={todo.isScheduleEvent ? `${styles.cellLink} ${styles.cellLinkWithIndicator}` : styles.cellLink}
			>
				{content}
			</NavLink>
		);
	};

	const renderScheduleDescriptionCell = (schedule: Record<string, unknown>) => {
		const description = String(schedule.description ?? 'N/A');
		const detailLocation = getTodoScheduleDetailLocation(String(schedule.id), {
			canEdit: canEditToDo,
		});

		return (
			<NavLink to={detailLocation} className={`${styles.cellLink} ${styles.cellLinkWithIndicator}`}>
				<Box
					display="flex"
					alignItems="center"
					gap={0.75}
					className={styles.descriptionWithIndicator}
				>
					<span className={styles.descriptionText}>{description}</span>
					{renderRecurringSeriesIndicator()}
				</Box>
			</NavLink>
		);
	};

	const renderViewActionButton = (todo: TodoTableRow) => (
		<Box className={styles.actionButtons}>
			<NavLink to={ROUTES.TO_DO_ID.replace(':id', String(todo.id))}>
				<IconButton size="small" sx={{ color: 'rgb(68, 99, 136)' }}>
					<VisibilityIcon style={{ fontSize: '20px' }} />
				</IconButton>
			</NavLink>
		</Box>
	);

	const renderCompletedOnTimeStatus = (todo: TodoTableRow) => {
		if (todo.onTime === true) {
			return <CheckCircleOutline sx={{ color: 'green', fontSize: '30px' }} />;
		}
		if (todo.onTime === false) {
			return <img src={PastDueLogo} alt="overdue" height="30px" />;
		}
		return null;
	};

	const renderActionButtons = (todo: TodoTableRow) => (
		<Box className={styles.actionButtons}>
			<NavLink to={ROUTES.TO_DO_ID.replace(':id', String(todo.id))}>
				<IconButton size="small" sx={{ color: 'rgb(68, 99, 136)' }}>
					<VisibilityIcon style={{ fontSize: '20px' }} />
				</IconButton>
			</NavLink>
			{canEditToDo ? (
				<NavLink to={ROUTES.EDIT_TO_DO.replace(':id', String(todo.id))}>
					<IconButton size="small" sx={{ color: 'rgb(68, 99, 136)' }}>
						<EditIcon style={{ fontSize: '20px' }} />
					</IconButton>
				</NavLink>
			) : (
				<IconButton size="small" sx={{ color: 'grey.400' }} disabled>
					<EditIcon style={{ fontSize: '20px' }} />
				</IconButton>
			)}
			<IconButton
				size="small"
				sx={{ color: canDeleteToDo ? 'red' : 'grey.400' }}
				onClick={() => handleDeleteClick(todo)}
				disabled={isDeleting || !canDeleteToDo}
			>
				<DeleteIcon style={{ fontSize: '20px' }} />
			</IconButton>
		</Box>
	);

	// Delete handlers
	const handleDeleteClick = (todo: any) => {
		setTodoToDelete(todo);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = () => {
		if (todoToDelete) {
			deleteTodo(todoToDelete.id);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setTodoToDelete(null);
	};

	const handleScheduleDeleteClick = (schedule: Record<string, unknown>) => {
		setScheduleToDelete(schedule);
		setScheduleDeleteDialogOpen(true);
	};

	const handleScheduleDeleteConfirm = () => {
		if (scheduleToDelete?.id) {
			deactivateSchedule(Number(scheduleToDelete.id));
		}
	};

	const handleScheduleDeleteCancel = () => {
		setScheduleDeleteDialogOpen(false);
		setScheduleToDelete(null);
	};

	const getScheduleCategoryDisplay = (schedule: Record<string, unknown>): string => {
		const category = schedule.category;
		if (!category) return 'Uncategorized';
		if (typeof category === 'string') return category;
		const categoryRecord = category as Record<string, unknown>;
		return String(categoryRecord.label ?? categoryRecord.name ?? 'Uncategorized');
	};

	// currentUserId for permission filtering
	const currentUserId = Number(localStorage.getItem('userId'));

	const filterForMyScope = (todos: unknown[]) =>
		filterTodosForMyScope(todos, currentUserId, userGroupIds, currentSecurityRoleId) as TodoTableRow[];

	// Filter todos based on permissions (security checks remain)
	// My Outstanding: show my_todos assigned to user or their assignment groups
	const outstandingMine = normalizeAndSortTodos(filterForMyScope(myTodos), priorityLookup);

	// All Outstanding: show outstanding_todos from API, filtered by permissions
	const allOutstandingToShow = canViewToDoAll
		? normalizeAndSortTodos(outstandingTodos, priorityLookup)
		: canViewToDoMy
			? outstandingMine
			: [];

	// Completed: show completed_todos from API, filtered by permissions
	const completedToShow = canViewToDoAll
		? normalizeAndSortTodos(completedTodos, priorityLookup, 'completedDateDesc')
		: canViewToDoMy
			? normalizeAndSortTodos(filterForMyScope(completedTodos), priorityLookup, 'completedDateDesc')
			: [];

	// Past Due: show past_due_todos from API, filtered by permissions
	const overdueToShow = canViewToDoAll
		? normalizeAndSortTodos(pastDueTodos, priorityLookup)
		: canViewToDoMy
			? normalizeAndSortTodos(filterForMyScope(pastDueTodos), priorityLookup)
			: [];


	return (
		<Box className={styles.pageContainer}>
			{/* Header */}
			<Box display="flex" justifyContent="end" alignItems="center" mb={2}>
				{canEditToDo && (
					<Button
						component={NavLink}
						to={ROUTES.ADD_TO_DO}
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: 'white',
							padding: '5px 15px',
							borderRadius: '4px',
							fontSize: '14px',
							cursor: 'pointer',
							textTransform: 'capitalize',
							textDecoration: 'none',
						}}
					>
						Add
					</Button>
				)}
			</Box>

			{/* Outstanding Tables */}
			<Box className={styles.splitTablesSection}>
				{/* Outstanding - Mine */}
				<Paper className={styles.splitTableCard}>
					<Typography variant="h6" className={styles.sectionTitle}>
						My Outstanding To-Dos
					</Typography>
					<TableContainer className={styles.tableWrapper}>
						<Table stickyHeader className={styles.todoTable}>
							<TableHead>
								<TableRow>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDescription}`}>
										<b>Description</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAssigned}`}>
										<b>Assigned</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colCategory}`}>
										<b>Category</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colPriority}`}>
										<b>Priority</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colStatus}`}>
										<b>Status</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDueDate}`}>
										<b>Due Date</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAction}`}>
										<b>Action</b>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!canViewToDoMy ? (
									<TableRow>
										<TableCell colSpan={7} align="center">
											You do not have permissions to view this!
										</TableCell>
									</TableRow>
								) : outstandingMine.length > 0 ? (
									outstandingMine.map((todo) => (
										<TableRow key={String(todo.id)}>
											<TableCell className={styles.colDescription}>
												{renderDescriptionCell(todo)}
											</TableCell>
											<TableCell className={styles.colAssigned}>{getAssignmentDisplay(todo)}</TableCell>
											<TableCell className={styles.colCategory}>{getCategoryDisplay(todo)}</TableCell>
											<TableCell className={styles.colPriority}>
												{getTodoPriorityDisplay(todo.priority, priorityLookup) ?? 'N/A'}
											</TableCell>
											<TableCell className={styles.colStatus}>
												{getTodoStatusDisplay(todo.status) ?? 'N/A'}
											</TableCell>
											<TableCell className={styles.colDueDate}>{formatTodoDateWithTime(todo)}</TableCell>
											<TableCell className={styles.colAction}>
												{renderActionButtons(todo)}
											</TableCell>
										</TableRow>
									))
								) : null}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>

				{/* Outstanding - All */}
				<Paper className={styles.splitTableCard}>
					<Typography variant="h6" className={styles.sectionTitle}>
						All Outstanding To-Dos
					</Typography>
					<TableContainer className={styles.tableWrapper}>
						<Table stickyHeader className={styles.todoTable}>
							<TableHead>
								<TableRow>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDescription}`}>
										<b>Description</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAssigned}`}>
										<b>Assigned</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colCategory}`}>
										<b>Category</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colPriority}`}>
										<b>Priority</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colStatus}`}>
										<b>Status</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDueDate}`}>
										<b>Due Date</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAction}`}>
										<b>Action</b>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!(canViewToDoAll || canViewToDoMy) ? (
									<TableRow>
										<TableCell colSpan={7} align="center">
											You do not have permissions to view this!
										</TableCell>
									</TableRow>
								) : allOutstandingToShow.length > 0 ? (
									allOutstandingToShow.map((todo) => (
										<TableRow key={String(todo.id)}>
											<TableCell className={styles.colDescription}>
												{renderDescriptionCell(todo)}
											</TableCell>
											<TableCell className={styles.colAssigned}>{getAssignmentDisplay(todo)}</TableCell>
											<TableCell className={styles.colCategory}>{getCategoryDisplay(todo)}</TableCell>
											<TableCell className={styles.colPriority}>
												{getTodoPriorityDisplay(todo.priority, priorityLookup) ?? 'N/A'}
											</TableCell>
											<TableCell className={styles.colStatus}>
												{getTodoStatusDisplay(todo.status) ?? 'N/A'}
											</TableCell>
											<TableCell className={styles.colDueDate}>{formatTodoDateWithTime(todo)}</TableCell>
											<TableCell className={styles.colAction}>
												{renderActionButtons(todo)}
											</TableCell>
										</TableRow>
									))
								) : null}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</Box>

			<Paper className={styles.fullWidthTableCard}>
				<Typography variant="h6" className={styles.sectionTitle}>
					Recurring To-Dos
				</Typography>
				<TableContainer className={styles.tableWrapper}>
				<Table stickyHeader className={styles.todoTable}>
					<TableHead>
						<TableRow>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colDescription}`}>
								<b>Description</b>
							</TableCell>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colAssigned}`}>
								<b>Assigned</b>
							</TableCell>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colCategory}`}>
								<b>Category</b>
							</TableCell>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colPriority}`}>
								<b>Priority</b>
							</TableCell>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colStatus}`}>
								<b>Frequency</b>
							</TableCell>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colDueDate}`}>
								<b>Schedule</b>
							</TableCell>
							<TableCell className={`${styles.tableHeaderCell} ${styles.colAction}`}>
								<b>Action</b>
							</TableCell>
						</TableRow>
					</TableHead>
					
					<TableBody>
						{!(canViewToDoAll || canViewToDoMy) ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									You do not have permissions to view this!
								</TableCell>
							</TableRow>
						) : recurringSchedules.length > 0 ? (
							recurringSchedules.map((schedule) => (
								<TableRow key={String(schedule.id)}>
									<TableCell className={styles.colDescription}>
										{renderScheduleDescriptionCell(schedule)}
									</TableCell>
									<TableCell className={styles.colAssigned}>
										{getScheduleAssignmentDisplay(schedule)}
									</TableCell>
									<TableCell className={styles.colCategory}>
										{getScheduleCategoryDisplay(schedule)}
									</TableCell>
									<TableCell className={styles.colPriority}>
										{getPriorityDisplayValue(schedule.priority, priorityLookup) ?? 'N/A'}
									</TableCell>
									<TableCell className={styles.colStatus}>
										{getScheduleFrequencyDisplay(schedule)}
									</TableCell>
									<TableCell className={styles.colDueDate}>
										{getScheduleTimeDisplay(schedule)}
									</TableCell>
									<TableCell className={styles.colAction}>
										<Box className={styles.actionButtons}>
											<NavLink
												to={ROUTES.VIEW_TO_DO_SCHEDULE.replace(':id', String(schedule.id))}
											>
												<IconButton size="small" sx={{ color: 'rgb(68, 99, 136)' }}>
													<VisibilityIcon style={{ fontSize: '20px' }} />
												</IconButton>
											</NavLink>
											{canEditToDo ? (
												<NavLink
													to={ROUTES.EDIT_TO_DO_SCHEDULE.replace(':id', String(schedule.id))}
												>
													<IconButton size="small" sx={{ color: 'rgb(68, 99, 136)' }}>
														<EditIcon style={{ fontSize: '20px' }} />
													</IconButton>
												</NavLink>
											) : (
												<IconButton size="small" sx={{ color: 'grey.400' }} disabled>
													<EditIcon style={{ fontSize: '20px' }} />
												</IconButton>
											)}
											<IconButton
												size="small"
												sx={{ color: canDeleteToDo ? 'red' : 'grey.400' }}
												onClick={() => handleScheduleDeleteClick(schedule)}
												disabled={isDeactivatingSchedule || !canDeleteToDo}
											>
												<DeleteIcon style={{ fontSize: '20px' }} />
											</IconButton>
										</Box>
									</TableCell>
								</TableRow>
							))
						) : null}
					</TableBody>
				</Table>
			</TableContainer>
			</Paper>

			{/* Completed and Overdue Tables */}
			<Box className={styles.splitTablesSection}>
				{/* Completed To-Do Table */}
				<Paper className={styles.splitTableCard}>
					<Typography variant="h6" className={styles.sectionTitle}>
						Completed To-Dos
					</Typography>
					<TableContainer className={styles.tableWrapper}>
						<Table stickyHeader className={styles.todoTable}>
							<TableHead>
								<TableRow>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDescription}`}>
										<b>Description</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAssigned}`}>
										<b>Assigned</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colCategory}`}>
										<b>Category</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colPriority}`}>
										<b>Priority</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colCompletedDate}`}>
										<b>Completed Date</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colOnTime}`}>
										<b>On Time / Overdue</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAction}`}>
										<b>Action</b>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!(canViewToDoAll || canViewToDoMy) ? (
									<TableRow>
										<TableCell colSpan={7} align="center">
											You do not have permissions to view this!
										</TableCell>
									</TableRow>
								) : completedToShow.length > 0 ? (
									completedToShow.map((todo) => {
										return (
											<TableRow key={String(todo.id)}>
												<TableCell className={styles.colDescription}>
													{renderDescriptionCell(todo)}
												</TableCell>
												<TableCell className={styles.colAssigned}>
													{getAssignmentDisplay(todo)}
												</TableCell>
												<TableCell className={styles.colCategory}>
													{getCategoryDisplay(todo)}
												</TableCell>
												<TableCell className={styles.colPriority}>
													{getTodoPriorityDisplay(todo.priority, priorityLookup) ?? 'N/A'}
												</TableCell>
												<TableCell className={styles.colCompletedDate}>
													{todo.completedAt ? formatDateMMDDYYYY(todo.completedAt) : 'N/A'}
												</TableCell>
												<TableCell className={styles.colOnTime}>
													{renderCompletedOnTimeStatus(todo)}
												</TableCell>
												<TableCell className={styles.colAction}>
													{renderViewActionButton(todo)}
												</TableCell>
											</TableRow>
										);
									})
								) : null}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>

				{/* Overdue To-Do Table */}
				<Paper className={styles.splitTableCard}>
					<Typography variant="h6" className={styles.sectionTitle}>
						Past Due To-Dos
					</Typography>
					<TableContainer className={styles.tableWrapper}>
						<Table stickyHeader className={styles.todoTable}>
							<TableHead>
								<TableRow>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDescription}`}>
										<b>Description</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAssigned}`}>
										<b>Assigned</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colCategory}`}>
										<b>Category</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colPriority}`}>
										<b>Priority</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colDueDate}`}>
										<b>Due Date</b>
									</TableCell>
									<TableCell className={`${styles.tableHeaderCell} ${styles.colAction}`}>
										<b>Action</b>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!(canViewToDoAll || canViewToDoMy) ? (
									<TableRow>
										<TableCell colSpan={6} align="center">
											You do not have permissions to view this!
										</TableCell>
									</TableRow>
								) : overdueToShow.length > 0 ? (
									overdueToShow.map((todo) => (
										<TableRow key={String(todo.id)}>
											<TableCell className={styles.colDescription}>
												{renderDescriptionCell(todo)}
											</TableCell>
											<TableCell className={styles.colAssigned}>
												{getAssignmentDisplay(todo)}
											</TableCell>
											<TableCell className={styles.colCategory}>
												{getCategoryDisplay(todo)}
											</TableCell>
											<TableCell className={styles.colPriority}>
												{getTodoPriorityDisplay(todo.priority, priorityLookup) ?? 'N/A'}
											</TableCell>
											<TableCell className={styles.colDueDate}>
												{formatTodoDateWithTime(todo)}
											</TableCell>
											<TableCell className={styles.colAction}>
												{renderActionButtons(todo)}
											</TableCell>
									</TableRow>
								))
							) : null}
						</TableBody>
					</Table>
				</TableContainer>
				</Paper>
			</Box>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleDeleteCancel}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={CONFIRM_DIALOG_PAPER_PROPS}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the to-do{' '}
						{todoToDelete?.description && (
							<strong>&ldquo;{todoToDelete.description}&rdquo;</strong>
						)}
						? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel} style={CONFIRM_DIALOG_BUTTON_STYLE}>
						Cancel
					</Button>
					<Button
						onClick={handleDeleteConfirm}
						style={CONFIRM_DIALOG_BUTTON_STYLE}
						disabled={isDeleting}
					>
						{isDeleting ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={scheduleDeleteDialogOpen}
				onClose={handleScheduleDeleteCancel}
				aria-labelledby="schedule-delete-dialog-title"
				aria-describedby="schedule-delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={CONFIRM_DIALOG_PAPER_PROPS}
			>
				<DialogTitle id="schedule-delete-dialog-title">Deactivate Recurring Series</DialogTitle>
				<DialogContent>
					<DialogContentText id="schedule-delete-dialog-description">
						Are you sure you want to deactivate the recurring to-do series &ldquo;
						{String(scheduleToDelete?.description ?? '')}&rdquo;? This will stop future occurrences
						from being generated.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleScheduleDeleteCancel} style={CONFIRM_DIALOG_BUTTON_STYLE}>
						Cancel
					</Button>
					<Button
						onClick={handleScheduleDeleteConfirm}
						style={CONFIRM_DIALOG_BUTTON_STYLE}
						disabled={isDeactivatingSchedule}
					>
						{isDeactivatingSchedule ? 'Deactivating...' : 'Deactivate Series'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default Todo;
